import { useCallback, useRef, useState } from 'react';
import { REACT_FLOW, VALUE_CHAIN_MAP } from 'constants/general';
import {
  BUSINESS_UNIT_TYPES, type TBusinessUnitType,
  VALUE_CHAIN_MAP_CUSTOM_NODES,
  VALUE_CHAIN_MAP_MODALS, VALUE_CHAIN_PANEL_NODES_TYPES,
  VALUE_CHAIN_UNIT_TYPES,
} from 'constants/interfaces';
import onDragOverByType from 'utils/dnd';
import { getBadRequestErrorMessage } from 'utils/helpers';
import {
  getTargetedBoard,
  getTargetedSwimlane,
  getUnitBusinessUnitType,
  getUnitEstimated,
  getUnitHeight,
  isTargetNodeMatching,
  readDragPointer,
} from 'utils/reactFlowHelpers';

import {
  getBoardBySection,
  getBoardForSwimlane,
  getPlacementErrorMessageKey,
  getPlacementErrorMessageKeyForUnitType,
  getSectionForPanelType,
  getSectionForUnitType,
  isSectionValidForPanelType,
  isSectionValidForUnitType,
} from '../utils/valueChainSectionValidation';

import type { Node } from '@xyflow/react';
import type { IUpdateBoardUnitRequest } from 'store/slices/valueChainMap/interfaces/IUpdateBoardUnitRequest';
import type { IValueChainBoardColumn } from 'store/slices/valueChainMap/interfaces/IValueChainBoardColumn';
import type { IValueChainUnit } from 'store/slices/valueChainMap/interfaces/IValueChainUnit';
import type { TNodeDragEvent } from 'utils/reactFlowHelpers';
import type { IUseDragAndDropParams } from '../interfaces/IUseDragAndDropParams';
import type { IUseDragAndDropReturn } from '../interfaces/IUseDragAndDropReturn';

const BOARD_RESTRICTED_UNIT_TYPES = new Set<string>([
  VALUE_CHAIN_UNIT_TYPES.PRODUCT,
  VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT,
  VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
  VALUE_CHAIN_UNIT_TYPES.CUSTOMER,
  VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET,
  VALUE_CHAIN_UNIT_TYPES.PROFIT_CENTER,
]);

const DRAG_DECORATIONS_RESET_PATCH: Record<string, unknown> = {
  isDragTarget: false,
  isValidSectionHighlight: false,
  isInvalidDropTarget: false,
  dropZoneType: undefined,
  draggingNodeType: undefined,
  tempHeight: undefined,
  tempYOffset: undefined,
  tempTotalChildrenHeight: undefined,
};

const UNIT_DRAG_STOP_RESET_PATCH: Record<string, unknown> = {
  isDragTarget: false,
  isValidSectionHighlight: false,
  isInvalidDropTarget: false,
  dropZoneType: undefined,
  draggingNodeType: undefined,
  tempYOffset: undefined,
  tempTotalChildrenHeight: undefined,
};

const DRAG_DECORATION_THROTTLE_MS = 16;

const useDragAndDrop = ({
  nodes,
  setNodes,
  screenToFlowPosition,
  valueChainMapId,
  updateBoardUnit,
  onUpdateSwimlaneOrderIndex,
  panelType,
  setPanelType,
  setCreatingTargetId,
  setActiveModal,
  closeModal,
  setCurrentTargetSwimlaneId,
  openProfitCenterModal,
  setSelectedProfitCenter,
  openCustomerModal,
  setSelectedCustomer,
  openSupplierModal,
  setSelectedSupplier,
  openBalanceSheetAssetsModal,
  setSelectedBalanceSheetAssets,
  openInputProductModal,
  setSelectedInputProduct,
  hasProfitCenter,
  t,
  toast,
}: IUseDragAndDropParams): IUseDragAndDropReturn => {
  const [targetNode, setTargetNode] = useState<Node | undefined>(undefined);
  const [draggingNode, setDraggingNode] = useState<Node | undefined>(undefined);
  const [draggingSwimlane, setDraggingSwimlane] = useState<Node | undefined>(undefined);
  const [targetSwimlane, setTargetSwimlane] = useState<Node | undefined>(undefined);
  const [swimlaneOriginalPosition, setSwimlaneOriginalPosition] = useState<{
    x: number, y:
    number, } | undefined>(undefined);
  const [targetUnitForReorder, setTargetUnitForReorder] = useState<Node | undefined>(undefined);
  const [targetUnitForNesting, setTargetUnitForNesting] = useState<Node | undefined>(undefined);
  const [unitOriginalPosition, setUnitOriginalPosition] = useState<{ x: number, y: number } | undefined>(undefined);
  const [unitOriginalParentId, setUnitOriginalParentId] = useState<string | undefined>(undefined);
  const [unitOriginalOrderIndex, setUnitOriginalOrderIndex] = useState<number | undefined>(undefined);
  const [creatingTargetId, setCreatingTargetIdState] = useState<number | undefined>(undefined);
  const lastDragDecorationTimeRef = useRef(0);

  // Applies drag decoration flags while preserving node identity: untouched nodes keep the same
  // object reference and an unchanged set returns the previous array, so React can bail out entirely.
  const applyDataPatchToNodes = (
    prevNodes: Node[],
    basePatch: Record<string, unknown>,
    overridesById?: Map<string, Record<string, unknown>>,
  ): Node[] => {
    let hasChanges = false;
    const nextNodes = prevNodes.map((prevNode) => {
      const override = overridesById?.get(prevNode.id);
      const patch = override ? { ...basePatch, ...override } : basePatch;
      const prevData = prevNode.data as Record<string, unknown>;
      const isAlreadyApplied = Object.entries(patch).every(([key, value]) => prevData[key] === value);

      if (isAlreadyApplied) {
        return prevNode;
      }

      hasChanges = true;
      return {
        ...prevNode,
        data: {
          ...prevData,
          ...patch,
        },
      };
    });

    return hasChanges ? nextNodes : prevNodes;
  };

  const getUnitNodeMeta = (node: Node): (IValueChainUnit & {
    countryGroupId?: string,
    isCountryGroupLeader?: boolean,
    isCountryGroupCollapsed?: boolean,
    isHiddenByCountryGroup?: boolean,
    isCollapsed?: boolean,
    isHiddenByParent?: boolean,
    swimlaneId?: string,
  }) | undefined => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return undefined;
    }
    return node.data as unknown as IValueChainUnit & {
      countryGroupId?: string,
      isCountryGroupLeader?: boolean,
      isCountryGroupCollapsed?: boolean,
      isHiddenByCountryGroup?: boolean,
      isHiddenByParent?: boolean,
      swimlaneId?: string,
    };
  };

  const isCountryGroupStackNode = (node: Node): boolean => {
    const unitData = getUnitNodeMeta(node);
    if (!unitData || !unitData.countryGroupId) {
      return false;
    }
    return Boolean(unitData.isCountryGroupCollapsed || unitData.isHiddenByCountryGroup);
  };

  const isAcceptingDropAsUnitTarget = (node: Node): boolean => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return true;
    }
    const unitData = getUnitNodeMeta(node);
    if (!unitData || !unitData.countryGroupId) {
      return true;
    }
    return !(unitData.isCountryGroupCollapsed || unitData.isHiddenByCountryGroup);
  };

  const isCollapsedCountryGroupLeader = (node: Node): boolean => {
    const unitData = getUnitNodeMeta(node);
    if (!unitData || !unitData.countryGroupId) {
      return false;
    }

    return Boolean(unitData.isCountryGroupLeader && unitData.isCountryGroupCollapsed);
  };

  const isAcceptingReorderAsUnitTarget = (node: Node): boolean => (
    isAcceptingDropAsUnitTarget(node) || isCollapsedCountryGroupLeader(node)
  );

  const getBoardNameBySwimlaneId = (swimlaneId?: string): string | undefined => {
    if (!swimlaneId) {
      return undefined;
    }

    const swimlaneNode = nodes.find(
      (n) => n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE && n.id === swimlaneId,
    );

    if (!swimlaneNode) {
      return undefined;
    }

    return getBoardForSwimlane(nodes, swimlaneNode)?.data?.name as string | undefined;
  };

  const canDropUnitTypeToBoard = (unitType?: string, boardName?: string): boolean => {
    if (!unitType) {
      return true;
    }

    if (!BOARD_RESTRICTED_UNIT_TYPES.has(unitType)) {
      return true;
    }

    return Boolean(boardName && isSectionValidForUnitType(boardName, unitType));
  };

  const showUnitTypePlacementError = (unitType?: string) => {
    if (!unitType) {
      return;
    }

    const errorKey = getPlacementErrorMessageKeyForUnitType(unitType);
    toast.error(t(`notifications.${errorKey}`));
  };

  const handleUpdateBoardUnit = (params: IUpdateBoardUnitRequest) => {
    updateBoardUnit(params).catch((error: unknown) => {
      const errorMessage = getBadRequestErrorMessage(error);
      toast.error(errorMessage || t('notifications.somethingWentWrong'));
    });
  };

  const MAX_NESTING_DEPTH = 4;

  const getPrimaryBusinessUnitType = (node: Node): TBusinessUnitType | undefined => (
    getUnitBusinessUnitType(node.data as unknown as Pick<IValueChainUnit, 'businessUnitType' | 'records'>)
  );

  const getNestingBusinessUnitType = (node: Node): TBusinessUnitType => (
    getPrimaryBusinessUnitType(node) ?? BUSINESS_UNIT_TYPES.PHYSICAL
  );

  // Helper function to calculate nesting depth
  const calculateNestingDepth = useCallback((nodeId: string, nodesList: Node[]): number => {
    const traverse = (id: string, currentDepth: number): number => {
      const currentNode = nodesList.find((n) => n.id === id);
      if (!currentNode || !currentNode.parentId) return currentDepth;

      const newDepth = currentNode.parentId.startsWith('unit_')
        ? currentDepth + 1
        : currentDepth;

      return traverse(currentNode.parentId, newDepth);
    };

    return traverse(nodeId, 0);
  }, []);

  const getSubtreeMaxDepthFromNode = useCallback((rootNodeId: string, nodesList: Node[]): number => {
    const traverse = (nodeId: string): number => {
      const children = nodesList.filter(
        (n) => n.parentId === nodeId && n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT,
      );

      if (!children.length) {
        return 0;
      }

      const childDepths = children.map((child) => traverse(child.id));

      return 1 + Math.max(...childDepths);
    };

    return traverse(rootNodeId);
  }, []);

  // Helper function to check if a node is a descendant of another node
  const isDescendant = useCallback((potentialDescendantId: string, ancestorId: string, nodesList: Node[]): boolean => {
    const traverse = (nodeId: string | undefined): boolean => {
      if (!nodeId) return false;
      if (nodeId === ancestorId) return true;

      const currentNode = nodesList.find((n) => n.id === nodeId);
      if (!currentNode || !currentNode.parentId) return false;

      if (currentNode.parentId.startsWith('unit_')) {
        return traverse(currentNode.parentId);
      }

      return false;
    };

    return traverse(potentialDescendantId);
  }, []);

  const getRootUnitsInSwimlane = (
    swimlaneNode: Node,
    nodesList: Node[],
    excludedNodeId?: string,
  ): Node[] => nodesList.filter((unitNode) => {
    if (unitNode.id === excludedNodeId || unitNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return false;
    }

    const unitData = getUnitNodeMeta(unitNode);
    if (!unitData) {
      return false;
    }

    return unitNode.parentId === swimlaneNode.id
      && unitData.swimlaneId === swimlaneNode.id
      && !unitData.isHiddenByParent;
  });

  const getVisibleRootUnitsInSwimlane = (
    swimlaneNode: Node,
    nodesList: Node[],
    excludedNodeId?: string,
  ): Node[] => getRootUnitsInSwimlane(swimlaneNode, nodesList, excludedNodeId)
    .filter((unitNode) => !getUnitNodeMeta(unitNode)?.isHiddenByCountryGroup);

  const getSwimlaneEndOrderIndex = (
    swimlaneNode: Node,
    nodesList: Node[],
    excludedNodeId?: string,
  ): { maxOrderIndex: number, nextOrderIndex: number } => {
    const rootUnits = getRootUnitsInSwimlane(swimlaneNode, nodesList, excludedNodeId);
    const maxOrderIndex = Math.max(
      ...rootUnits.map((unitNode) => Number(unitNode.data.orderIndex) || 0),
      -1,
    );

    return {
      maxOrderIndex,
      nextOrderIndex: maxOrderIndex + 1,
    };
  };

  const onDragOver = useCallback((event: React.DragEvent<HTMLLIElement>) => {
    onDragOverByType({
      event, panelType, nodes, setNodes, screenToFlowPosition,
    });
    if (
      panelType
      && panelType !== VALUE_CHAIN_PANEL_NODES_TYPES.FILTER
      && panelType !== VALUE_CHAIN_PANEL_NODES_TYPES.SWIMLANE
    ) {
      const dragPosition = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const section = getSectionForPanelType(panelType);
      setNodes((prevNodes) => {
        const validBoards = section
          ? prevNodes.filter(
            (prevNode) => prevNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
              && typeof (prevNode.data as { name?: string }).name === 'string'
              && isSectionValidForPanelType(
                (prevNode.data as { name?: string }).name as string,
                panelType,
              ),
          )
          : [];

        const validBoardIds = new Set(validBoards.map((board) => board.id));

        const targetedSwimlaneForBoard = getTargetedSwimlane(dragPosition, prevNodes);
        const boardFromSwimlane = targetedSwimlaneForBoard
          ? getBoardForSwimlane(prevNodes, targetedSwimlaneForBoard) : undefined;
        const boardUnderCursor = getTargetedBoard(dragPosition, prevNodes) || boardFromSwimlane;
        let hasChanges = false;
        const nextNodes = prevNodes.map((prevNode) => {
          if (prevNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD) return prevNode;
          const boardName = prevNode.data?.name as string | undefined;
          const isWrongBoard = boardUnderCursor
            && prevNode.id === boardUnderCursor.id
            && boardName
            && !isSectionValidForPanelType(boardName, panelType);
          const isValidSection = validBoardIds.has(prevNode.id);
          const prevData = prevNode.data as Record<string, unknown>;

          if (
            prevData.isValidSectionHighlight === (isValidSection === true)
            && prevData.isInvalidDropTarget === (isWrongBoard === true)
          ) {
            return prevNode;
          }

          hasChanges = true;
          return {
            ...prevNode,
            data: {
              ...prevData,
              isValidSectionHighlight: isValidSection === true,
              isInvalidDropTarget: isWrongBoard === true,
            },
          };
        });

        return hasChanges ? nextNodes : prevNodes;
      });
    }
  }, [nodes, panelType, screenToFlowPosition, setNodes]);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLLIElement>) => {
      event.preventDefault();

      if (panelType) {
        const dragPosition = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        if (panelType === VALUE_CHAIN_PANEL_NODES_TYPES.SWIMLANE) {
          const targetedBoard = getTargetedBoard(dragPosition, nodes);
          if (targetedBoard) {
            const targetId = Number(targetedBoard?.data.id);
            setCreatingTargetIdState(targetId);
            setCreatingTargetId(targetId);
            setActiveModal(VALUE_CHAIN_MAP_MODALS.ADD_SWIMLANE);
          }
          setPanelType('');
          return;
        }

        const cardPanelTypes = [
          VALUE_CHAIN_PANEL_NODES_TYPES.PROFIT_CENTER,
          VALUE_CHAIN_PANEL_NODES_TYPES.CUSTOMER,
          VALUE_CHAIN_PANEL_NODES_TYPES.SUPPLIER,
          VALUE_CHAIN_PANEL_NODES_TYPES.BALANCE_SHEET_ASSET,
          VALUE_CHAIN_PANEL_NODES_TYPES.INPUT_PRODUCT,
        ];
        if (cardPanelTypes.includes(panelType)) {
          const targetedSwimlane = getTargetedSwimlane(dragPosition, nodes);
          if (!targetedSwimlane) {
            toast.error(t('notifications.valueChainPlacementOutsideSection'));
            setPanelType('');
            closeModal();
            return;
          }
          const targetBoard = getBoardForSwimlane(nodes, targetedSwimlane);
          const boardName = targetBoard?.data?.name as string | undefined;
          if (!boardName || !isSectionValidForPanelType(boardName, panelType)) {
            toast.error(t(`notifications.${getPlacementErrorMessageKey(panelType)}`));
            setPanelType('');
            closeModal();
            return;
          }

          if (!hasProfitCenter && panelType !== VALUE_CHAIN_PANEL_NODES_TYPES.PROFIT_CENTER) {
            toast.error(t('notifications.profitCenterRequiredBeforeEntityCreation'));
            setPanelType('');
            closeModal();
            return;
          }
        }

        if (panelType === VALUE_CHAIN_PANEL_NODES_TYPES.PROFIT_CENTER) {
          const targetedSwimlane = getTargetedSwimlane(dragPosition, nodes);
          if (targetedSwimlane) {
            setCurrentTargetSwimlaneId(Number(targetedSwimlane?.data.id));
            setActiveModal(VALUE_CHAIN_MAP_MODALS.ADD_PROFIT_CENTER);
            openProfitCenterModal();
            setSelectedProfitCenter(undefined);
          }
        }

        if (panelType === VALUE_CHAIN_PANEL_NODES_TYPES.CUSTOMER) {
          const targetedSwimlane = getTargetedSwimlane(dragPosition, nodes);
          if (targetedSwimlane) {
            setCurrentTargetSwimlaneId(Number(targetedSwimlane?.data.id));
            setActiveModal(VALUE_CHAIN_MAP_MODALS.ADD_CUSTOMER);
            openCustomerModal();
            setSelectedCustomer(undefined);
          }
        }

        if (panelType === VALUE_CHAIN_PANEL_NODES_TYPES.SUPPLIER) {
          const targetedSwimlane = getTargetedSwimlane(dragPosition, nodes);
          if (targetedSwimlane) {
            setCurrentTargetSwimlaneId(Number(targetedSwimlane?.data.id));
            setActiveModal(VALUE_CHAIN_MAP_MODALS.ADD_SUPPLIER);
            openSupplierModal();
            setSelectedSupplier(undefined);
          }
        }
        if (panelType === VALUE_CHAIN_PANEL_NODES_TYPES.BALANCE_SHEET_ASSET) {
          const targetedSwimlane = getTargetedSwimlane(dragPosition, nodes);
          if (targetedSwimlane) {
            setCurrentTargetSwimlaneId(Number(targetedSwimlane?.data.id));
            setActiveModal(VALUE_CHAIN_MAP_MODALS.ADD_BALANCE_SHEET_ASSET);
            openBalanceSheetAssetsModal();
            setSelectedBalanceSheetAssets(undefined);
          }
        }
        if (panelType === VALUE_CHAIN_PANEL_NODES_TYPES.INPUT_PRODUCT) {
          const targetedSwimlane = getTargetedSwimlane(dragPosition, nodes);
          if (targetedSwimlane) {
            setCurrentTargetSwimlaneId(Number(targetedSwimlane?.data.id));
            setActiveModal(VALUE_CHAIN_MAP_MODALS.ADD_INPUT_PRODUCT);
            openInputProductModal();
            setSelectedInputProduct(undefined);
          }
        }
        setPanelType('');
      }
    },
    [closeModal, t, panelType, screenToFlowPosition, setPanelType, nodes, openProfitCenterModal,
      setSelectedProfitCenter, setCurrentTargetSwimlaneId, openCustomerModal, setSelectedCustomer,
      openSupplierModal, setSelectedSupplier, openBalanceSheetAssetsModal, setSelectedBalanceSheetAssets,
      openInputProductModal, setSelectedInputProduct, setCreatingTargetId, setActiveModal, hasProfitCenter, toast],
  );

  const onNodeDrag = (event: TNodeDragEvent, node: Node) => {
    event.preventDefault();

    const now = performance.now();
    if (now - lastDragDecorationTimeRef.current < DRAG_DECORATION_THROTTLE_MS) {
      return;
    }
    lastDragDecorationTimeRef.current = now;

    if (node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE) {
      const dragPosition = screenToFlowPosition(readDragPointer(event));
      const draggingBoardId = node.data.boardId;

      const swimlaneNodes = nodes
        .filter((n) => n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE
          && n.id !== node.id
          && n.data.boardId === draggingBoardId);

      const targetedSwimlane = swimlaneNodes.find((n) => {
        const swimlaneWidth = Number(n.measured?.width) || VALUE_CHAIN_MAP.SWIMLANE_WIDTH;
        const calculatedHeight = n.data.accumulatedMaxBoardHeight
          ? Number(n.data.accumulatedMaxBoardHeight) - VALUE_CHAIN_MAP.TITLE_HEIGHT - VALUE_CHAIN_MAP.BOARD_PADDING
          : Number(n.measured?.height) || 800;
        return (
          dragPosition.x >= n.position.x
          && dragPosition.x <= n.position.x + swimlaneWidth
          && dragPosition.y >= n.position.y
          && dragPosition.y <= n.position.y + calculatedHeight
        );
      });

      setNodes((prevNodes) => applyDataPatchToNodes(
        prevNodes,
        DRAG_DECORATIONS_RESET_PATCH,
        targetedSwimlane
          ? new Map([[targetedSwimlane.id, { isDragTarget: true }]])
          : undefined,
      ));
      setTargetSwimlane(targetedSwimlane);
      return;
    }

    if (isCountryGroupStackNode(node)) {
      return;
    }

    const dragPosition = screenToFlowPosition(readDragPointer(event));

    const nodeById = new Map<string, Node>(nodes.map((n) => [n.id, n]));
    const unitChildIdsByParentId = new Map<string, string[]>();
    nodes.forEach((n) => {
      if (n.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT || !n.parentId) {
        return;
      }

      const childIds = unitChildIdsByParentId.get(n.parentId);
      if (childIds) {
        childIds.push(n.id);
      } else {
        unitChildIdsByParentId.set(n.parentId, [n.id]);
      }
    });

    const collectDescendantUnitIds = (rootId: string): string[] => {
      const descendantIds: string[] = [];
      const idsToVisit = [...(unitChildIdsByParentId.get(rootId) || [])];

      while (idsToVisit.length > 0) {
        const currentId = idsToVisit.pop();
        if (currentId) {
          descendantIds.push(currentId);
          idsToVisit.push(...(unitChildIdsByParentId.get(currentId) || []));
        }
      }

      return descendantIds;
    };

    const nestingDepthById = new Map<string, number>();
    const getNestingDepth = (nodeId: string): number => {
      const cachedDepth = nestingDepthById.get(nodeId);
      if (cachedDepth !== undefined) {
        return cachedDepth;
      }

      const currentNode = nodeById.get(nodeId);
      const parentId = currentNode?.parentId;
      const depth = parentId
        ? getNestingDepth(parentId) + (parentId.startsWith('unit_') ? 1 : 0)
        : 0;

      nestingDepthById.set(nodeId, depth);
      return depth;
    };

    const getSubtreeMaxDepth = (rootId: string): number => {
      const childIds = unitChildIdsByParentId.get(rootId) || [];
      if (!childIds.length) {
        return 0;
      }

      return 1 + Math.max(...childIds.map(getSubtreeMaxDepth));
    };

    const getAncestorUnitIdsFromMap = (nodeId: string): string[] => {
      const ancestors: string[] = [];
      let currentId: string | undefined = nodeId;

      while (currentId) {
        const currentNode: Node | undefined = nodeById.get(currentId);
        const parentId = currentNode?.parentId;
        if (!parentId || !parentId.startsWith('unit_')) {
          break;
        }

        const parentNode = nodeById.get(parentId);
        if (!parentNode || parentNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
          break;
        }

        ancestors.push(parentId);
        currentId = parentId;
      }

      return ancestors;
    };

    const draggingSubtreeIds = new Set<string>([node.id, ...collectDescendantUnitIds(node.id)]);

    const calculateAbsolutePosition = (currentNode: Node): { x: number, y: number } => {
      const traverse = (nodeId: string | undefined, accX: number, accY: number): { x: number, y: number } => {
        if (!nodeId) return { x: accX, y: accY };

        const parentNode = nodeById.get(nodeId);
        if (!parentNode) return { x: accX, y: accY };

        const newX = accX + parentNode.position.x;
        const newY = accY + parentNode.position.y;

        if (parentNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE) {
          return { x: newX, y: newY };
        }

        return traverse(parentNode.parentId, newX, newY);
      };

      return traverse(currentNode.parentId, currentNode.position.x, currentNode.position.y);
    };

    const absoluteX = dragPosition.x;
    const absoluteY = dragPosition.y;
    const centerX = absoluteX;
    const centerY = absoluteY;

    const currentSwimlaneId = node.data.swimlaneId;
    const currentParentId = node.parentId;

    const siblingUnits = nodes.filter((n) => n.id !== node.id
      && n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT
      && n.data.swimlaneId === currentSwimlaneId
      && n.parentId === currentParentId
      && isAcceptingReorderAsUnitTarget(n));

    const isDraggingLogicalForHover = getPrimaryBusinessUnitType(node) === BUSINESS_UNIT_TYPES.LOGICAL;

    const draggedUnitTypeForSection = node.data?.type as string | undefined;
    const sectionForHover = draggedUnitTypeForSection
      ? getSectionForUnitType(draggedUnitTypeForSection)
      : undefined;
    const validBoardForHover = sectionForHover
      ? getBoardBySection(nodes, sectionForHover)
      : undefined;

    let allUnitsForHover: Node[];
    if (isDraggingLogicalForHover) {
      allUnitsForHover = nodes.filter(
        (n) => n.id !== node.id && n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT,
      );
    } else if (!validBoardForHover) {
      // No strict board section found (custom board names) — allow dragging to any unit
      // of the same type across all boards so cross-board reordering works with visual feedback.
      allUnitsForHover = nodes.filter(
        (n) => n.id !== node.id
          && n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT
          && String(n.data?.type || '') === String(node.data?.type || ''),
      );
    } else {
      const boardBySwimlaneId = new Map<string, Node | undefined>();
      const getBoardForSwimlaneCached = (swimlaneNode: Node): Node | undefined => {
        if (boardBySwimlaneId.has(swimlaneNode.id)) {
          return boardBySwimlaneId.get(swimlaneNode.id);
        }

        const board = getBoardForSwimlane(nodes, swimlaneNode);
        boardBySwimlaneId.set(swimlaneNode.id, board);
        return board;
      };

      allUnitsForHover = nodes.filter((n) => {
        if (n.id === node.id || n.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) return false;
        const swimlaneIdForHover = n.data?.swimlaneId as string | undefined;
        const swNode = swimlaneIdForHover ? nodeById.get(swimlaneIdForHover) : undefined;
        if (!swNode) return false;
        const board = getBoardForSwimlaneCached(swNode);
        return board?.id === validBoardForHover.id;
      });
    }

    allUnitsForHover = allUnitsForHover.filter(
      (unitNode) => !draggingSubtreeIds.has(unitNode.id)
        && isAcceptingReorderAsUnitTarget(unitNode)
        && !(unitNode.data as { isHiddenByParent?: boolean }).isHiddenByParent
        && !(unitNode.data as { isHiddenByCountryGroup?: boolean }).isHiddenByCountryGroup,
    );

    const unitsWithDepth = allUnitsForHover.map((n) => ({
      node: n,
      depth: getNestingDepth(n.id),
    })).sort((a, b) => b.depth - a.depth);

    const getVisualUnitHeight = (unitNode: Node): number => {
      const unitData = getUnitNodeMeta(unitNode);
      if (unitData?.isCountryGroupLeader && unitData.isCountryGroupCollapsed) {
        return VALUE_CHAIN_MAP.STACKED_GROUP_HEADER_HEIGHT;
      }

      const businessUnitType = unitData ? getUnitBusinessUnitType(unitData) : undefined;
      const estimated = unitData ? getUnitEstimated(unitData) : undefined;
      const unitType = unitNode.data.type as string;
      const baseHeight = getUnitHeight(
        unitType,
        businessUnitType,
        estimated,
      );

      const totalChildrenHeight = Number(unitNode.data.totalChildrenHeight) || 0;
      const isCollapsed = Boolean(unitNode.data.isCollapsed);

      if (isCollapsed) {
        const hasLogicalStatus = businessUnitType === BUSINESS_UNIT_TYPES.LOGICAL || Boolean(estimated);
        const baseHeaderHeight = VALUE_CHAIN_MAP.CORRECTED_PROFIT_CENTER_HEIGHT;
        const statusHeight = hasLogicalStatus ? VALUE_CHAIN_MAP.LOGICAL_STATUS_HEIGHT : 0;
        return baseHeaderHeight + statusHeight;
      }

      return baseHeight
        + VALUE_CHAIN_MAP.UNIT_RECORD_PADDING
        + totalChildrenHeight
        + VALUE_CHAIN_MAP.UNIT_CONTAINER_MARGIN_BOTTOM;
    };

    const getTrailingDropSwimlane = (): Node | undefined => {
      const targetedSwimlaneNode = getTargetedSwimlane({ x: centerX, y: centerY }, nodes);
      const draggedSwimlaneId = String(currentSwimlaneId || '');

      if (!targetedSwimlaneNode || targetedSwimlaneNode.id !== draggedSwimlaneId) {
        return undefined;
      }

      if (node.parentId?.startsWith('unit_')) {
        return undefined;
      }

      const rootUnits = getVisibleRootUnitsInSwimlane(targetedSwimlaneNode, nodes, node.id);
      if (!rootUnits.length) {
        return undefined;
      }

      const lastVisibleRootBottom = Math.max(...rootUnits.map((rootUnit) => {
        const { y: rootUnitAbsoluteY } = calculateAbsolutePosition(rootUnit);
        return rootUnitAbsoluteY + getVisualUnitHeight(rootUnit);
      }));

      return centerY >= lastVisibleRootBottom ? targetedSwimlaneNode : undefined;
    };

    const hoveredUnit: Node | undefined = unitsWithDepth.find(({ node: n }) => {
      const { x: targetAbsoluteX, y: targetAbsoluteY } = calculateAbsolutePosition(n);

      const unitWidth = Number(n.measured?.width) || 252;
      const visualUnitHeight = getVisualUnitHeight(n);

      return (
        absoluteX >= targetAbsoluteX
        && absoluteX <= targetAbsoluteX + unitWidth
        && absoluteY >= targetAbsoluteY
        && absoluteY <= targetAbsoluteY + visualUnitHeight
      );
    })?.node;

    if (!hoveredUnit) {
      setTargetUnitForReorder(undefined);
      setTargetUnitForNesting(undefined);
    } else {
      const hovered = hoveredUnit;
      const { y: targetAbsoluteY } = calculateAbsolutePosition(hovered);
      const baseUnitHeight = getVisualUnitHeight(hovered);

      const draggingBusinessUnitType = getNestingBusinessUnitType(node);
      const targetBusinessUnitType = getNestingBusinessUnitType(hovered);
      const isDraggingPhysical = draggingBusinessUnitType === BUSINESS_UNIT_TYPES.PHYSICAL;
      const isDraggingLogical = draggingBusinessUnitType === BUSINESS_UNIT_TYPES.LOGICAL;
      const isTargetLogical = targetBusinessUnitType === BUSINESS_UNIT_TYPES.LOGICAL;
      const isTargetPhysical = targetBusinessUnitType === BUSINESS_UNIT_TYPES.PHYSICAL;
      const isSibling = siblingUnits.some((sibling) => sibling.id === hovered.id);
      const isLogicalOnPhysicalReorder = isDraggingLogical && isTargetPhysical;
      const isHoveredCollapsedGroupStack = isCollapsedCountryGroupLeader(hovered);
      const hoveredUnitMeta = getUnitNodeMeta(hovered);
      const isTargetCollapsedUnit = Boolean(hoveredUnitMeta?.isCollapsed);

      const isCrossColumnReorder = !isSibling;

      // Pre-compute nesting compatibility so we can use it in isReorderZone.
      const draggingTypeEarly = node.data.type as string;
      const targetTypeEarly = hovered.data.type as string;
      const isTypeAllowedForNestingEarly = (draggingTypeEarly === VALUE_CHAIN_UNIT_TYPES.SUPPLIER
          && targetTypeEarly === VALUE_CHAIN_UNIT_TYPES.SUPPLIER)
        || (draggingTypeEarly === VALUE_CHAIN_UNIT_TYPES.CUSTOMER
          && targetTypeEarly === VALUE_CHAIN_UNIT_TYPES.CUSTOMER);
      const canNestByBusinessUnitTypeEarly = isDraggingPhysical
        || (isDraggingLogical && isTargetLogical);

      const reorderZoneRatio = isLogicalOnPhysicalReorder ? 0.35 : 0.30;
      const topThreshold = targetAbsoluteY + baseUnitHeight * reorderZoneRatio;
      // For cross-column drags where nesting is structurally impossible (e.g. profit-center),
      // treat the entire card as a reorder zone so the drag never falls through to closeNode.
      const nestingImpossibleCrossColumn = isCrossColumnReorder
        && (!isTypeAllowedForNestingEarly || !canNestByBusinessUnitTypeEarly)
        && !isLogicalOnPhysicalReorder;
      const isReorderZone = absoluteY <= topThreshold
        || isLogicalOnPhysicalReorder
        || isHoveredCollapsedGroupStack
        || isTargetCollapsedUnit
        || nestingImpossibleCrossColumn;

      if (isReorderZone && (isSibling || isLogicalOnPhysicalReorder || isCrossColumnReorder)) {
        const reorderTargetDepth = getNestingDepth(hovered.id);
        const reorderSubtreeDepth = getSubtreeMaxDepth(node.id);
        const wouldExceedMaxDepthForReorder = reorderTargetDepth + reorderSubtreeDepth > MAX_NESTING_DEPTH;

        if (!wouldExceedMaxDepthForReorder) {
          const draggingNodeHeight = getVisualUnitHeight(node);
          const offsetValue = draggingNodeHeight + 8;
          const targetOrderIndex = Number(hovered.data.orderIndex) || 0;
          const draggingOrderIndex = Number(node.data.orderIndex) || 0;

          const nodesToShift = new Set<string>();

          if (isSibling) {
            const isMovingDownWithinSameParent = draggingOrderIndex < targetOrderIndex;

            nodes.forEach((prevNode) => {
              if (prevNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) return;
              if (prevNode.id === node.id) return;
              if (prevNode.parentId !== currentParentId) return;
              if (prevNode.data.swimlaneId !== currentSwimlaneId) return;

              const prevNodeOrderIndex = Number(prevNode.data.orderIndex) || 0;

              if (isMovingDownWithinSameParent) {
                // Moving down: show insertion gap at target position (and below),
                // not from the original source card position.
                if (prevNodeOrderIndex < targetOrderIndex) return;
              } else if (
                prevNodeOrderIndex < targetOrderIndex
                || prevNodeOrderIndex >= draggingOrderIndex
              ) {
                return;
              }

              nodesToShift.add(prevNode.id);
              collectDescendantUnitIds(prevNode.id).forEach((descendantId) => {
                nodesToShift.add(descendantId);
              });
            });
          } else if (isLogicalOnPhysicalReorder) {
            nodesToShift.add(hovered.id);
            collectDescendantUnitIds(hovered.id).forEach((descendantId) => {
              nodesToShift.add(descendantId);
            });
          } else if (isCrossColumnReorder) {
            const targetParentId = hovered.parentId;
            nodes.forEach((prevNode) => {
              if (prevNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) return;
              if (prevNode.id === node.id) return;
              if (prevNode.parentId !== targetParentId) return;

              const orderIndex = Number(prevNode.data.orderIndex) || 0;
              if (orderIndex < targetOrderIndex) return;

              nodesToShift.add(prevNode.id);
              collectDescendantUnitIds(prevNode.id).forEach((descendantId) => {
                nodesToShift.add(descendantId);
              });
            });
          }

          const ancestorIdsToExpand = new Set<string>();
          nodesToShift.forEach((shiftedId) => {
            getAncestorUnitIdsFromMap(shiftedId).forEach((ancestorId) => ancestorIdsToExpand.add(ancestorId));
          });

          setNodes((prevNodes) => {
            let hasChanges = false;
            const nextNodes = prevNodes.map((prevNode) => {
              const isHovered = prevNode.id === hovered.id;
              const shouldSkipShift = draggingSubtreeIds.has(prevNode.id);
              const shouldShift = !shouldSkipShift && nodesToShift.has(prevNode.id);
              const isAncestorToExpand = ancestorIdsToExpand.has(prevNode.id);
              const prevData = prevNode.data as Record<string, unknown>;
              const baseChildrenHeight = Number(prevData.totalChildrenHeight) || 0;
              const patch = {
                ...DRAG_DECORATIONS_RESET_PATCH,
                ...(isHovered ? {
                  isDragTarget: true,
                  dropZoneType: 'reorder',
                  draggingNodeType: node.data.type,
                } : {}),
                tempTotalChildrenHeight: isAncestorToExpand ? baseChildrenHeight + offsetValue : undefined,
                tempYOffset: shouldShift ? offsetValue : undefined,
              };
              const isAlreadyApplied = Object.entries(patch).every(([key, value]) => prevData[key] === value);

              if (isAlreadyApplied) {
                return prevNode;
              }

              hasChanges = true;
              return {
                ...prevNode,
                data: {
                  ...prevData,
                  ...patch,
                },
              };
            });

            return hasChanges ? nextNodes : prevNodes;
          });

          setTargetUnitForReorder(hovered);
          setTargetUnitForNesting(undefined);
          setTargetNode(undefined);
          return;
        }
      }

      const isTypeAllowedForNesting = isTypeAllowedForNestingEarly;
      const canNestByBusinessUnitType = canNestByBusinessUnitTypeEarly;

      const targetDepth = getNestingDepth(hovered.id);
      const subtreeDepthFromDragging = getSubtreeMaxDepth(node.id);
      const wouldExceedMaxDepth = targetDepth + 1 + subtreeDepthFromDragging > MAX_NESTING_DEPTH;
      const isNestingIntoSelf = node.id === hovered.id;
      const isNestingIntoDescendant = draggingSubtreeIds.has(hovered.id);
      const isLogicalInPhysical = isDraggingLogical && isTargetPhysical;

      const isHoveringOwnParent = hovered.id === node.parentId;
      const canNest = isTypeAllowedForNesting
        && canNestByBusinessUnitType
        && !isLogicalInPhysical
        && !isHoveredCollapsedGroupStack
        && !isTargetCollapsedUnit
        && !wouldExceedMaxDepth
        && !isNestingIntoSelf
        && !isNestingIntoDescendant
        && !isHoveringOwnParent;

      // Collapsed card: ONLY swap/reorder, never drop inside. Do not allow nesting.
      if (!isLogicalOnPhysicalReorder && !isTargetCollapsedUnit && absoluteY > topThreshold && canNest) {
        setNodes((prevNodes) => {
          let hasChanges = false;
          const nextNodes = prevNodes.map((prevNode) => {
            const prevData = prevNode.data as Record<string, unknown>;
            // Defensive: never show nested drop zone on a collapsed card (e.g. after zoom/position preserve)
            const isNestingTarget = prevNode.id === hovered.id && !prevData.isCollapsed;
            const patch = isNestingTarget
              ? {
                ...DRAG_DECORATIONS_RESET_PATCH,
                isDragTarget: true,
                dropZoneType: 'nested',
                draggingNodeType: node.data.type,
              }
              : DRAG_DECORATIONS_RESET_PATCH;
            const isAlreadyApplied = Object.entries(patch).every(([key, value]) => prevData[key] === value);

            if (isAlreadyApplied) {
              return prevNode;
            }

            hasChanges = true;
            return {
              ...prevNode,
              data: {
                ...prevData,
                ...patch,
              },
            };
          });

          return hasChanges ? nextNodes : prevNodes;
        });

        setTargetUnitForNesting(hovered);
        setTargetUnitForReorder(undefined);
        setTargetNode(undefined);
        return;
      }
    }

    setTargetUnitForReorder(undefined);
    setTargetUnitForNesting(undefined);

    const trailingDropSwimlane = getTrailingDropSwimlane();
    const isMatchingDropTarget = (candidateNode: Node): boolean => (
      isTargetNodeMatching(candidateNode, node)
      || candidateNode.id === trailingDropSwimlane?.id
    );

    const closeNode = nodes.find((n) => {
      let result = false;
      if (n.id !== node.id) {
        if (n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE) {
          const calculatedHeight = n.data.accumulatedMaxBoardHeight
            ? Number(n.data.accumulatedMaxBoardHeight) - VALUE_CHAIN_MAP.TITLE_HEIGHT - VALUE_CHAIN_MAP.BOARD_PADDING
            : Number(n.measured?.height) || 800;
          const swimlaneWidth = Number(n.measured?.width) || VALUE_CHAIN_MAP.SWIMLANE_WIDTH;

          result = (
            centerX > n.position.x
            && centerX < n.position.x + swimlaneWidth
            && centerY > n.position.y
            && centerY < n.position.y + calculatedHeight
          );
        } else if (n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
          // Unit nodes have positions relative to their swimlane parent.
          // We must use absolute (flow-world) coordinates to avoid false matches.
          const { x: absX, y: absY } = calculateAbsolutePosition(n);
          result = Math.abs(centerX - absX) < REACT_FLOW.DRAG_N_DROP_ENOUGH_DISTANCE
            && Math.abs(centerY - absY) < REACT_FLOW.DRAG_N_DROP_ENOUGH_DISTANCE;
        }
      }
      return result;
    });

    let highlightTargetNode: Node | undefined;
    if (closeNode && isMatchingDropTarget(closeNode)) {
      highlightTargetNode = closeNode;
    } else {
      const targetedSwimlane = getTargetedSwimlane({ x: centerX, y: centerY }, nodes);
      highlightTargetNode = targetedSwimlane && isMatchingDropTarget(targetedSwimlane)
        ? targetedSwimlane
        : undefined;
    }

    setTargetNode(highlightTargetNode);

    const targetedSwimlaneForBoard = getTargetedSwimlane({ x: centerX, y: centerY }, nodes);
    const boardFromSwimlane = targetedSwimlaneForBoard
      ? getBoardForSwimlane(nodes, targetedSwimlaneForBoard) : undefined;
    const boardUnderCursor = getTargetedBoard({ x: centerX, y: centerY }, nodes) || boardFromSwimlane;
    const draggingUnitTypeForBoard = node.data?.type as string | undefined;

    const validBoardIds = draggingUnitTypeForBoard && BOARD_RESTRICTED_UNIT_TYPES.has(draggingUnitTypeForBoard)
      ? new Set(nodes
        .filter((boardNode) => {
          if (boardNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD) {
            return false;
          }

          const boardName = boardNode.data?.name as string | undefined;
          return canDropUnitTypeToBoard(draggingUnitTypeForBoard, boardName);
        })
        .map((boardNode) => boardNode.id))
      : new Set<string>();

    const decorationOverridesById = new Map<string, Record<string, unknown>>();

    if (highlightTargetNode) {
      decorationOverridesById.set(highlightTargetNode.id, { isDragTarget: true });
    }

    nodes.forEach((boardNode) => {
      if (boardNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD) return;

      const boardName = boardNode.data?.name as string | undefined;
      const isBoardUnderCursor = boardUnderCursor?.id === boardNode.id;
      const isAllowedTarget = canDropUnitTypeToBoard(draggingUnitTypeForBoard, boardName);
      const isInvalidDropTarget = isBoardUnderCursor && !isAllowedTarget;

      decorationOverridesById.set(boardNode.id, {
        isValidSectionHighlight: !isInvalidDropTarget
          && (validBoardIds.has(boardNode.id) || (isBoardUnderCursor && isAllowedTarget)),
        isInvalidDropTarget,
      });
    });

    setNodes((prevNodes) => applyDataPatchToNodes(
      prevNodes,
      DRAG_DECORATIONS_RESET_PATCH,
      decorationOverridesById,
    ));
  };

  const onNodeDragStart = (event: TNodeDragEvent, node: Node) => {
    event.preventDefault();

    setTargetNode(undefined);
    setTargetUnitForReorder(undefined);
    setTargetUnitForNesting(undefined);

    if (node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE) {
      setDraggingSwimlane(node);
      setSwimlaneOriginalPosition({ x: node.position.x, y: node.position.y });
      return;
    }

    if (isCountryGroupStackNode(node)) {
      return;
    }

    setDraggingNode(node);
    setUnitOriginalPosition({ x: node.position.x, y: node.position.y });
    setUnitOriginalParentId(node.parentId);
    setUnitOriginalOrderIndex(Number(node.data.orderIndex) || 0);
    const draggedUnitType = node.data?.type as string | undefined;
    const draggedBusinessUnitType = getUnitBusinessUnitType(
      node.data as unknown as Pick<IValueChainUnit, 'businessUnitType' | 'records'>,
    );
    const isLogicalUnit = draggedUnitType === BUSINESS_UNIT_TYPES.LOGICAL
      || draggedBusinessUnitType === BUSINESS_UNIT_TYPES.LOGICAL;

    if (draggedUnitType) {
      const section = getSectionForUnitType(draggedUnitType);
      if (section) {
        setNodes((prevNodes) => {
          const validBoard = getBoardBySection(prevNodes, section);
          if (!validBoard) return prevNodes;
          return prevNodes.map((prevNode) => (prevNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
            ? {
              ...prevNode,
              data: {
                ...prevNode.data,
                isValidSectionHighlight: prevNode.id === validBoard.id,
                isInvalidDropTarget: false,
              },
            }
            : prevNode));
        });
      } else if (isLogicalUnit && node.parentId) {
        setNodes((prevNodes) => {
          const swimlane = prevNodes.find((n) => n.id === node.parentId);
          const validBoard = swimlane ? getBoardForSwimlane(prevNodes, swimlane) : undefined;
          if (!validBoard) return prevNodes;
          return prevNodes.map((prevNode) => (prevNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
            ? {
              ...prevNode,
              data: {
                ...prevNode.data,
                isValidSectionHighlight: prevNode.id === validBoard.id,
                isInvalidDropTarget: false,
              },
            }
            : prevNode));
        });
      }
    }
  };

  const onNodeDragStop = (event: TNodeDragEvent, node: Node) => {
    event.preventDefault();

    if (node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE) {
      setNodes((prevNodes) => applyDataPatchToNodes(prevNodes, DRAG_DECORATIONS_RESET_PATCH));

      if (targetSwimlane && draggingSwimlane) {
        const currentSwimlane = draggingSwimlane.data as unknown as IValueChainBoardColumn;
        const targetSwimlaneData = targetSwimlane.data as unknown as IValueChainBoardColumn;

        const draggingBoardId = draggingSwimlane.data.boardId;
        const targetBoardId = targetSwimlane.data.boardId;

        if (draggingBoardId === targetBoardId) {
          const newOrderIndex = targetSwimlaneData.orderIndex;

          if (newOrderIndex !== currentSwimlane.orderIndex) {
            onUpdateSwimlaneOrderIndex(newOrderIndex, currentSwimlane);
          }
        } else {
          setNodes((prevNodes) => prevNodes.map((prevNode) => (
            prevNode.id === draggingSwimlane.id
              ? { ...prevNode, position: swimlaneOriginalPosition || prevNode.position }
              : prevNode
          )));
          toast.error(t('notifications.cannotMoveSwimlaneAcrossBoards'));
        }
      } else if (draggingSwimlane && swimlaneOriginalPosition) {
        setNodes((prevNodes) => prevNodes.map((prevNode) => (
          prevNode.id === draggingSwimlane.id
            ? { ...prevNode, position: swimlaneOriginalPosition }
            : prevNode
        )));
      }

      setDraggingSwimlane(undefined);
      setTargetSwimlane(undefined);
      setSwimlaneOriginalPosition(undefined);
      return;
    }

    setNodes((prevNodes) => applyDataPatchToNodes(prevNodes, UNIT_DRAG_STOP_RESET_PATCH));

    if (targetUnitForNesting && draggingNode) {
      // Use current nodes state so we never nest into a collapsed card (e.g. after zoom)
      const currentTargetNode = nodes.find((n) => n.id === targetUnitForNesting.id);
      const targetMeta = currentTargetNode ? getUnitNodeMeta(currentTargetNode) : undefined;
      const isTargetCollapsedUnit = Boolean(targetMeta?.isCollapsed);

      const draggingBusinessUnitType = getNestingBusinessUnitType(draggingNode);
      const targetBusinessUnitType = getNestingBusinessUnitType(targetUnitForNesting);
      const isDraggingPhysical = draggingBusinessUnitType === BUSINESS_UNIT_TYPES.PHYSICAL;
      const isDraggingLogical = draggingBusinessUnitType === BUSINESS_UNIT_TYPES.LOGICAL;
      const isTargetLogical = targetBusinessUnitType === BUSINESS_UNIT_TYPES.LOGICAL;
      const isTargetPhysical = targetBusinessUnitType === BUSINESS_UNIT_TYPES.PHYSICAL;

      const draggingType = draggingNode.data.type;
      const targetType = targetUnitForNesting.data.type;
      const isTypeAllowedForNesting = (draggingType === VALUE_CHAIN_UNIT_TYPES.SUPPLIER
          && targetType === VALUE_CHAIN_UNIT_TYPES.SUPPLIER)
        || (draggingType === VALUE_CHAIN_UNIT_TYPES.CUSTOMER
          && targetType === VALUE_CHAIN_UNIT_TYPES.CUSTOMER);

      const targetDepth = calculateNestingDepth(targetUnitForNesting.id, nodes);
      const subtreeDepthFromDragging = getSubtreeMaxDepthFromNode(draggingNode.id, nodes);
      const wouldExceedMaxDepth = targetDepth + 1 + subtreeDepthFromDragging > MAX_NESTING_DEPTH;
      const isNestingIntoSelf = draggingNode.id === targetUnitForNesting.id;
      const isNestingIntoDescendant = isDescendant(targetUnitForNesting.id, draggingNode.id, nodes);
      const canNestByBusinessUnitType = isDraggingPhysical
        || (isDraggingLogical && isTargetLogical);
      const isLogicalInPhysical = isDraggingLogical && isTargetPhysical;

      const canNest = isTypeAllowedForNesting
        && canNestByBusinessUnitType
        && !isLogicalInPhysical
        && !isTargetCollapsedUnit
        && !wouldExceedMaxDepth
        && !isNestingIntoSelf
        && !isNestingIntoDescendant;

      if (!canNest) {
        if (wouldExceedMaxDepth) {
          toast.error(t('notifications.valueChainMaxNestingDepthExceeded'));
        }
        setNodes((prevNodes) => prevNodes.map((prevNode) => (
          prevNode.id === draggingNode?.id
            ? { ...prevNode, position: unitOriginalPosition || prevNode.position }
            : prevNode
        )));
        setTargetUnitForNesting(undefined);
        setDraggingNode(undefined);
        setUnitOriginalPosition(undefined);
        setUnitOriginalParentId(undefined);
        setUnitOriginalOrderIndex(undefined);
        return;
      }

      const draggingUnitType = draggingNode.data.type as string | undefined;
      const targetSwimlaneId = String(targetUnitForNesting.data?.swimlaneId || '');
      const targetBoardName = getBoardNameBySwimlaneId(targetSwimlaneId);
      const isBoardValidForUnitType = canDropUnitTypeToBoard(draggingUnitType, targetBoardName);

      if (!isBoardValidForUnitType) {
        showUnitTypePlacementError(draggingUnitType);
        setNodes((prevNodes) => prevNodes.map((prevNode) => (
          prevNode.id === draggingNode?.id
            ? { ...prevNode, position: unitOriginalPosition || prevNode.position }
            : prevNode
        )));
        setTargetUnitForNesting(undefined);
        setDraggingNode(undefined);
        setUnitOriginalPosition(undefined);
        setUnitOriginalParentId(undefined);
        setUnitOriginalOrderIndex(undefined);
        return;
      }

      const targetId = Number(targetUnitForNesting?.data?.id);
      const targetParentId = `unit_${targetId}`;

      if (unitOriginalParentId === targetParentId && unitOriginalOrderIndex !== undefined) {
        setNodes((prevNodes) => prevNodes.map((prevNode) => (
          prevNode.id === draggingNode?.id
            ? {
              ...prevNode,
              position: unitOriginalPosition || prevNode.position,
              parentId: unitOriginalParentId,
              data: {
                ...prevNode.data,
                orderIndex: unitOriginalOrderIndex,
              },
            }
            : prevNode
        )));
        setTargetUnitForNesting(undefined);
        setDraggingNode(undefined);
        setUnitOriginalPosition(undefined);
        setUnitOriginalParentId(undefined);
        setUnitOriginalOrderIndex(undefined);
        return;
      }

      handleUpdateBoardUnit({
        unitId: Number(draggingNode?.data.id),
        columnId: null,
        parentId: targetId,
        valueChainMapId: Number(valueChainMapId),
        orderIndex: 0,
      });

      setTargetUnitForNesting(undefined);
      setDraggingNode(undefined);
      setUnitOriginalPosition(undefined);
      return;
    }

    if (targetUnitForReorder && draggingNode) {
      const isTargetDescendantOfDragging = isDescendant(
        targetUnitForReorder.id,
        draggingNode.id,
        nodes,
      );

      if (isTargetDescendantOfDragging) {
        setNodes((prevNodes) => prevNodes.map((prevNode) => (
          prevNode.id === draggingNode.id
            ? { ...prevNode, position: unitOriginalPosition || prevNode.position }
            : prevNode
        )));
        setTargetUnitForReorder(undefined);
        setDraggingNode(undefined);
        setUnitOriginalPosition(undefined);
        setUnitOriginalParentId(undefined);
        setUnitOriginalOrderIndex(undefined);
        return;
      }

      const reorderTargetDepth = calculateNestingDepth(targetUnitForReorder.id, nodes);
      const reorderSubtreeDepth = getSubtreeMaxDepthFromNode(draggingNode.id, nodes);
      const wouldExceedMaxDepthForReorder = reorderTargetDepth + reorderSubtreeDepth > MAX_NESTING_DEPTH;

      if (wouldExceedMaxDepthForReorder) {
        toast.error(t('notifications.valueChainMaxNestingDepthExceeded'));
        setNodes((prevNodes) => prevNodes.map((prevNode) => (
          prevNode.id === draggingNode.id
            ? { ...prevNode, position: unitOriginalPosition || prevNode.position }
            : prevNode
        )));
        setTargetUnitForReorder(undefined);
        setDraggingNode(undefined);
        setUnitOriginalPosition(undefined);
        setUnitOriginalParentId(undefined);
        setUnitOriginalOrderIndex(undefined);
        return;
      }

      const currentUnit = draggingNode.data as unknown as IValueChainUnit;
      const targetUnit = targetUnitForReorder.data as unknown as IValueChainUnit;
      const newOrderIndex = targetUnit.orderIndex;
      const sameParent = draggingNode.parentId === targetUnitForReorder.parentId;

      if (!sameParent || newOrderIndex !== currentUnit.orderIndex) {
        const draggingUnitType = draggingNode.data.type as string | undefined;
        const targetSwimlaneIdForValidation = String(targetUnitForReorder.data?.swimlaneId || '');
        const targetBoardName = getBoardNameBySwimlaneId(targetSwimlaneIdForValidation);
        const isBoardValidForUnitType = canDropUnitTypeToBoard(draggingUnitType, targetBoardName);

        if (!isBoardValidForUnitType) {
          showUnitTypePlacementError(draggingUnitType);
          setNodes((prevNodes) => prevNodes.map((prevNode) => (
            prevNode.id === draggingNode.id
              ? { ...prevNode, position: unitOriginalPosition || prevNode.position }
              : prevNode
          )));
          setTargetUnitForReorder(undefined);
          setDraggingNode(undefined);
          setUnitOriginalPosition(undefined);
          setUnitOriginalParentId(undefined);
          setUnitOriginalOrderIndex(undefined);
          return;
        }

        const targetParentId = targetUnitForReorder.parentId;
        const parentId = targetParentId?.startsWith('unit_')
          ? Number(targetParentId.split('_')[1])
          : null;
        const targetSwimlaneId = String(targetUnitForReorder.data?.swimlaneId || '').split('_')[1];
        const swimlaneIdNum = targetSwimlaneId ? Number(targetSwimlaneId) : null;
        const columnId = parentId != null ? null : swimlaneIdNum;

        handleUpdateBoardUnit({
          unitId: Number(draggingNode.data.id),
          columnId,
          parentId,
          valueChainMapId: Number(valueChainMapId),
          orderIndex: newOrderIndex || 0,
        });
      }

      setTargetUnitForReorder(undefined);
      setDraggingNode(undefined);
      setUnitOriginalPosition(undefined);
      return;
    }

    if (targetNode && draggingNode) {
      if (targetNode.id === draggingNode.id) {
        setNodes((prevNodes) => prevNodes.map((prevNode) => (
          prevNode.id === draggingNode.id
            ? { ...prevNode, position: unitOriginalPosition || prevNode.position }
            : prevNode
        )));
        setTargetNode(undefined);
        setDraggingNode(undefined);
        setUnitOriginalPosition(undefined);
        setUnitOriginalParentId(undefined);
        setUnitOriginalOrderIndex(undefined);
        return;
      }

      const isTargetSwimlane = targetNode?.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE;
      const isTargetUnit = targetNode?.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT;

      const targetId = Number(targetNode?.data?.id);
      const currentColumnId = (draggingNode.data as { columnId?: number | null }).columnId ?? null;
      const currentParentId = (draggingNode.data as { parentId?: number | null }).parentId ?? null;

      const targetUnitMeta = isTargetUnit ? getUnitNodeMeta(targetNode) : undefined;
      const isTargetCollapsedUnit = Boolean(targetUnitMeta?.isCollapsed);

      let nextColumnId: number | null = null;
      if (isTargetSwimlane) {
        nextColumnId = targetId;
      } else if (isTargetCollapsedUnit) {
        const targetSwimlaneId = String(targetNode.data?.swimlaneId || '').split('_')[1];
        nextColumnId = targetSwimlaneId ? Number(targetSwimlaneId) : null;
      }

      const nextParentId = isTargetSwimlane || isTargetCollapsedUnit ? null : targetId;

      const draggingUnitType = draggingNode.data.type as string | undefined;
      const targetBoardName = isTargetSwimlane
        ? getBoardForSwimlane(nodes, targetNode)?.data?.name as string | undefined
        : getBoardNameBySwimlaneId(String(targetNode.data?.swimlaneId || ''));
      const isBoardValidForUnitType = canDropUnitTypeToBoard(draggingUnitType, targetBoardName);

      if (!isBoardValidForUnitType) {
        showUnitTypePlacementError(draggingUnitType);
        setNodes((prevNodes) => prevNodes.map((prevNode) => (
          prevNode.id === draggingNode.id
            ? { ...prevNode, position: unitOriginalPosition || prevNode.position }
            : prevNode
        )));
        setTargetNode(undefined);
        setDraggingNode(undefined);
        setUnitOriginalPosition(undefined);
        setUnitOriginalParentId(undefined);
        setUnitOriginalOrderIndex(undefined);
        return;
      }

      let newOrderIndex = 0;
      let maxOrderIndexInTargetSwimlane: number | undefined;
      if (isTargetSwimlane) {
        const endOrderIndex = getSwimlaneEndOrderIndex(targetNode, nodes, draggingNode.id);
        maxOrderIndexInTargetSwimlane = endOrderIndex.maxOrderIndex;
        newOrderIndex = endOrderIndex.nextOrderIndex;
      } else if (isTargetCollapsedUnit && targetUnitMeta) {
        newOrderIndex = targetUnitMeta.orderIndex;
      } else {
        newOrderIndex = Number(draggingNode?.data.orderIndex) || 0;
      }

      const currentOrderIndex = Number(draggingNode?.data.orderIndex) || 0;
      const isSameRootSwimlane = isTargetSwimlane && draggingNode.parentId === targetNode.id;
      const isAlreadyLastRootInSwimlane = isSameRootSwimlane
        && maxOrderIndexInTargetSwimlane !== undefined
        && currentOrderIndex >= maxOrderIndexInTargetSwimlane;
      const isSamePlacementTarget = currentColumnId === nextColumnId && currentParentId === nextParentId;
      const shouldSkipBoardUnitUpdate = isAlreadyLastRootInSwimlane
        || (isSamePlacementTarget && newOrderIndex === currentOrderIndex);

      if (shouldSkipBoardUnitUpdate) {
        setNodes((prevNodes) => prevNodes.map((prevNode) => (
          prevNode.id === draggingNode.id
            ? { ...prevNode, position: unitOriginalPosition || prevNode.position }
            : prevNode
        )));
        setTargetNode(undefined);
        setDraggingNode(undefined);
        setUnitOriginalPosition(undefined);
        setUnitOriginalParentId(undefined);
        setUnitOriginalOrderIndex(undefined);
        return;
      }

      handleUpdateBoardUnit({
        unitId: Number(draggingNode?.data.id),
        columnId: nextColumnId,
        parentId: nextParentId,
        valueChainMapId: Number(valueChainMapId),
        orderIndex: newOrderIndex,
      });

      setTargetNode(undefined);
      setDraggingNode(undefined);
      setUnitOriginalPosition(undefined);
      return;
    }

    if (draggingNode && unitOriginalPosition) {
      const dropPosition = screenToFlowPosition(readDragPointer(event));
      const targetedSwimlaneAtDrop = getTargetedSwimlane(dropPosition, nodes);

      if (!targetedSwimlaneAtDrop) {
        toast.error(t('notifications.valueChainPlacementOutsideSection'));
      }
      setNodes((prevNodes) => prevNodes.map((prevNode) => (
        prevNode.id === draggingNode?.id
          ? { ...prevNode, position: unitOriginalPosition }
          : prevNode
      )));
    }

    setDraggingNode(undefined);
    setTargetNode(undefined);
    setTargetUnitForReorder(undefined);
    setTargetUnitForNesting(undefined);
    setUnitOriginalPosition(undefined);
  };

  return {
    onNodeDrag,
    onNodeDragStart,
    onNodeDragStop,
    onDragOver,
    onDrop,
    creatingTargetId,
  };
};

export default useDragAndDrop;
